// Audit & usage (02-DESIGN/03-console/01-the-console.md § the surfaces, §
// conventions; hq decision 0048): the console keeps no audit page of its own.
// The one entry under Administration opens the observability solution —
// where it is installed, the panels it declares as the page's tabs, drawn
// from its declaration as any solution's panel is
// (02-DESIGN/14-solutions/03-the-console.md); where it is not, its catalogue
// entry with *Install*. The workspace's sign-in records are the solution's
// to show, in its trail. A declared list's row opens the record's page,
// `audit/<panel>/<key>`, with a crumb back to the panel's tab; a declared
// form shows in the tab's place (14-solutions/03 § the elements; hq
// decision 0069). The tab is kept in the history entry's state, never the
// address, so Back from a record returns to the tab it was opened from.

import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { Empty, NotAllowed, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { useFormState } from "../../ui/form.tsx";
import { DeclaredForm, Elements, RecordPage } from "../solutions/Elements.tsx";

import { OBSERVABILITY, readInstalled, useInstalled } from "../solutions/installed.ts";
import { drawnFor, openForm, panelsShown, recordList } from "../solutions/panel.ts";
import { panelLine } from "../solutions/PanelLine.tsx";
import { useGrants, useGrantsRead, type Grant } from "../solutions/sends.ts";
import { NotInstalled } from "../solutions/NotInstalled.tsx";
import { Chips, usePanelSelection } from "../solutions/Selection.tsx";
import { useSurface } from "../surface.tsx";
import { Tabs, useTab, type TabSpec } from "../../ui/tabs.tsx";
import type { Manifest, Panel } from "../../wire/solutions.ts";

const TITLE = "Audit & usage";

/** tabAsked is the panel a history entry's state names as its tab, `{ tab }`, or nothing. */
export function tabAsked(state: unknown): string | undefined {
  const t = (state as { tab?: unknown } | null)?.tab;
  return typeof t === "string" && t !== "" ? t : undefined;
}

/**
 * AuditRecordPage is a record of one of the observability solution's panels,
 * `audit/<panel>/<key>`: the record's page, its crumb back to *Audit & usage*
 * on that panel's tab.
 */
export function AuditRecordPage() {
  const { panel = "", key = "" } = useParams();
  const s = useSurface();
  const [installed] = useLoad(`audit/record/installed/${s.tenant}`, () => readInstalled(s.bus));
  const admin = s.offered("solutions:manage");
  const { grants, ready: grantsRead } = useGrantsRead(s.bus, admin);
  const obs = installed.state === "ready" ? installed.value.find((r) => r.name === OBSERVABILITY) : undefined;
  const m = obs?.manifest;
  const ids = obs?.workloads ?? {};
  const p = m === undefined ? undefined : panelsShown(m, ids, admin, grants).find((x) => x.name === panel);
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "audit")} state={{ tab: panel }}>
        {TITLE}
      </Link>
      {p !== undefined && (
        <>
          <span>/</span>
          <Link to={tenantPath(s.tenant, "audit")} state={{ tab: panel }}>
            {p.title}
          </Link>
        </>
      )}
    </>
  );
  // Which panels exist here is decided once the grants have answered (Forwarding exists only where one sends).
  if (installed.state === "pending" || !grantsRead) {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the solution" />
      </>
    );
  }
  if (installed.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={TITLE} />
        <NotAllowed err={installed.err} what="Installed solutions" />
      </>
    );
  }
  const list =
    m === undefined || p === undefined ? undefined : recordList(drawnFor(m, p.elements, admin, ids, grants));
  if (m === undefined || p === undefined || list === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title="Not a record" />
        <Empty>{TITLE} shows no record here.</Empty>
      </>
    );
  }
  return <RecordPage ctx={{ m, ids, admin }} list={list} recordKey={key} crumb={crumb} />;
}

export function AuditPage() {
  const s = useSurface();
  const installed = useInstalled(s.bus);
  const [installErr, setInstallErr] = useState<unknown>(undefined);
  const obs = installed.find((r) => r.name === OBSERVABILITY);
  const m = obs?.manifest;
  if (obs === undefined || m === undefined) {
    return (
      <>
        {installErr !== undefined && <NotAllowed err={installErr} />}
        <NotInstalled name={OBSERVABILITY} title={TITLE} onInstalled={setInstallErr} />
      </>
    );
  }
  return <Panels m={m} ids={obs.workloads ?? {}} />;
}

/** Panels is the installed solution's panels drawn for the person, one tab each. */
function Panels({ m, ids }: { m: Manifest; ids: Readonly<Record<string, string>> }) {
  const s = useSurface();
  const admin = s.offered("solutions:manage");
  // Forwarding declares `sends`, and exists only where a grant sends (hq issue 185).
  const grants = useGrants(s.bus, admin);
  const panels = panelsShown(m, ids, admin, grants);
  if (panels.length === 0) {
    return (
      <>
        <PageHead title={TITLE} />
        <Empty>{m.display} does not run in this workspace, so there is nothing to show.</Empty>
      </>
    );
  }
  return <Tabbed m={m} ids={ids} admin={admin} panels={panels} grants={grants} />;
}

/**
 * Tabbed is the page's tabs, one per panel, opening on the first; a tab is a
 * view of the page and not in the address (§ conventions). A single panel is
 * one concern and is drawn without tabs.
 */
function Tabbed({
  m,
  ids,
  admin,
  panels,
  grants,
}: {
  m: Manifest;
  ids: Readonly<Record<string, string>>;
  admin: boolean;
  panels: readonly Panel[];
  grants: readonly Grant[];
}) {
  const s = useSurface();
  const location = useLocation();
  const navigate = useNavigate();
  const forms = useFormState();
  const tabs: TabSpec<string>[] = panels.map((p) => ({ id: p.name, label: p.title }));
  const [tab, setTabState] = useTab(tabs, tabAsked(location.state));
  // The tab rides in the entry's state, so Back from a record finds it again.
  const setTab = (t: string) => {
    setTabState(t);
    const st = location.state as Record<string, unknown> | null;
    void navigate(location.pathname + location.search, {
      replace: true,
      state: { ...(st ?? {}), tab: t, form: undefined },
    });
  };
  const current = panels.find((p) => p.name === tab) ?? panels[0];
  // A panel's selection narrows it, as on a panel's own page (14-solutions/03 § the elements).
  const { selection, select, clear } = usePanelSelection(current);
  if (current === undefined) {
    return null;
  }
  const elements = drawnFor(m, current.elements, admin, ids, grants);
  const ctx = {
    m,
    ids,
    admin,
    selection,
    select,
    recordTo: (key: string) => tenantPath(s.tenant, "audit", current.name, key),
  };
  const form = openForm(forms.open, elements);
  const drawn =
    form !== undefined ? (
      <DeclaredForm ctx={ctx} e={form} back={current.title} heading="h2" onClose={forms.close} />
    ) : (
      <>
        <Chips
          p={current}
          selection={selection}
          onRemove={(field) => {
            select(field);
          }}
          onClear={clear}
        />
        <Elements key={current.name} ctx={ctx} elements={elements} />
      </>
    );
  return (
    <>
      <PageHead title={TITLE} line={panelLine(m, current, grants)} />
      {panels.length > 1 ? (
        <Tabs label={TITLE} tabs={tabs} value={tab} onChange={setTab}>
          {drawn}
        </Tabs>
      ) : (
        drawn
      )}
    </>
  );
}
